/* SportswearBay, global.js (ES module, deferred by default)
   Tiny core: header/menu/dropdowns/search dialog/video facade + on-demand module loader.
   Everything else lives in feature modules that load only when their markup exists. */

const doc = document;
const root = doc.documentElement;
const SWB = window.SWB || { modules: {} };

/* ---------- On-demand modules ---------- */
const loaded = new Map();
function load(name) {
  const url = SWB.modules[name];
  if (!url) return Promise.resolve(null);
  if (!loaded.has(name)) loaded.set(name, import(url).catch((e) => { console.error('[swb] module', name, e); return null; }));
  return loaded.get(name);
}
function initModules(scope = doc) {
  scope.querySelectorAll('[data-module]').forEach((el) => {
    if (el.dataset.moduleReady) return;
    el.dataset.moduleReady = '1';
    el.dataset.module.split(' ').forEach((name) => {
      load(name).then((m) => m && typeof m.default === 'function' && m.default(el));
    });
  });
}
SWB.load = load;
window.SWB = SWB;

/* ---------- Mobile menu ---------- */
const header = doc.querySelector('[data-header]');
const toggle = doc.querySelector('[data-menu-toggle]');
const nav = doc.getElementById('HeaderNav');

function setMenu(open) {
  if (!toggle || !nav) return;
  if (open) {
    // one layout read, before any writes
    const bottom = header ? Math.round(header.getBoundingClientRect().bottom) : 0;
    root.style.setProperty('--nav-top', bottom + 'px');
  }
  root.classList.toggle('menu-open', open);
  toggle.setAttribute('aria-expanded', String(open));
  const main = doc.getElementById('MainContent');
  if (main) main.inert = open;
  if (open) {
    const first = nav.querySelector('a, summary, button');
    if (first) requestAnimationFrame(() => first.focus());
  } else {
    toggle.focus();
  }
}

/* ---------- Search dialog ---------- */
const searchDialog = doc.getElementById('SearchDialog');
function openSearch() {
  if (!searchDialog) return;
  if (root.classList.contains('menu-open')) setMenu(false);
  searchDialog.showModal();
  const input = searchDialog.querySelector('input[type=search]');
  if (input) input.focus();
  if (input && input.hasAttribute('data-predictive')) load('search').then((m) => m && m.default(searchDialog));
}

/* ---------- Delegated events (one listener each) ---------- */
doc.addEventListener('click', (e) => {
  const t = e.target;
  if (!(t instanceof Element)) return;

  if (t.closest('[data-menu-toggle]')) {
    setMenu(!root.classList.contains('menu-open'));
    return;
  }
  if (t.closest('[data-search-open]')) { e.preventDefault(); openSearch(); return; }
  if (t.closest('[data-search-close]')) { searchDialog && searchDialog.close(); return; }
  if (searchDialog && t === searchDialog) { searchDialog.close(); return; } // backdrop click

  // Video facade: swap poster for iframe only on demand
  const play = t.closest('.video__play');
  if (play && play.dataset.src) {
    const iframe = doc.createElement('iframe');
    iframe.src = play.dataset.src;
    iframe.title = play.dataset.title || 'Video';
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    iframe.allowFullscreen = true;
    play.parentElement.replaceChildren(iframe);
    return;
  }

  // Close open desktop dropdowns when clicking outside them
  const openDd = doc.querySelectorAll('.menu__dd[open]');
  if (openDd.length) openDd.forEach((d) => { if (!d.contains(t)) d.open = false; });
});

/* ---------- Desktop dropdowns: open on hover (mouse only, >=990px) ----------
   Keyboard & touch keep native <details> click/Enter behaviour. On hover devices a click on the
   top-level item goes to its "View all" link, like a normal menu link. */
const hoverMQ = matchMedia('(hover: hover) and (pointer: fine) and (min-width: 990px)');
let ddTimer;
const isMinimal = () => header && header.classList.contains('header--minimal');
doc.addEventListener('pointerover', (e) => {
  if (e.pointerType !== 'mouse' || !hoverMQ.matches || isMinimal()) return;
  const dd = e.target instanceof Element && e.target.closest('.header .menu__dd');
  if (!dd) return;
  clearTimeout(ddTimer);
  if (!dd.open) dd.open = true;
});
doc.addEventListener('pointerout', (e) => {
  if (e.pointerType !== 'mouse' || !hoverMQ.matches || isMinimal()) return;
  const dd = e.target instanceof Element && e.target.closest('.header .menu__dd[open]');
  if (!dd || dd.contains(e.relatedTarget)) return;
  clearTimeout(ddTimer);
  ddTimer = setTimeout(() => { dd.open = false; }, 180);
});
doc.addEventListener('click', (e) => {
  if (!hoverMQ.matches || isMinimal() || e.detail === 0) return; // e.detail 0 = keyboard activation
  const summary = e.target instanceof Element && e.target.closest('.header .menu__dd > summary');
  if (!summary) return;
  e.preventDefault(); // stay open instead of toggling closed
  const url = summary.parentElement.dataset.url;
  if (url) location.href = url;
}, true);

// Only one dropdown open at a time ('toggle' does not bubble -> capture)
doc.addEventListener('toggle', (e) => {
  const d = e.target;
  if (!(d instanceof HTMLDetailsElement) || !d.open || !d.classList.contains('menu__dd')) return;
  doc.querySelectorAll('.menu__dd[open]').forEach((o) => { if (o !== d && !o.contains(d)) o.open = false; });
}, true);

doc.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return;
  const dd = e.target instanceof Element && e.target.closest('.menu__dd[open]');
  if (dd) { dd.open = false; dd.querySelector('summary').focus(); return; }
  if (root.classList.contains('menu-open')) setMenu(false);
});

/* ---------- Transparent header -> solid on scroll (one IntersectionObserver, no scroll listener) ---------- */
if (header && header.classList.contains('header--transparent') && header.classList.contains('header--sticky') && 'IntersectionObserver' in window) {
  const sentinel = doc.createElement('div');
  sentinel.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:60px;pointer-events:none;visibility:hidden';
  sentinel.setAttribute('aria-hidden', 'true');
  doc.body.prepend(sentinel);
  new IntersectionObserver(([entry]) => header.classList.toggle('is-solid', !entry.isIntersecting)).observe(sentinel);
}

/* ---------- Sidebar accordion menu + generic panel toggles ---------- */
doc.addEventListener('click', (e) => {
  const t = e.target instanceof Element ? e.target : null;
  if (!t) return;
  const btn = t.closest('.side-menu__btn');
  if (btn) {
    const open = btn.getAttribute('aria-expanded') !== 'true';
    const item = btn.closest('.side-menu__item');
    // close sibling branches at the same level
    item.parentElement.querySelectorAll(':scope > .side-menu__item > .side-menu__row > .side-menu__btn[aria-expanded="true"]').forEach((b) => {
      if (b === btn) return;
      b.setAttribute('aria-expanded', 'false');
      const sub = doc.getElementById(b.getAttribute('aria-controls'));
      if (sub) sub.hidden = true;
    });
    btn.setAttribute('aria-expanded', String(open));
    const sub = doc.getElementById(btn.getAttribute('aria-controls'));
    if (sub) sub.hidden = !open;
    return;
  }
  const tog = t.closest('[data-panel-toggle]');
  if (tog) {
    const panel = doc.getElementById(tog.getAttribute('aria-controls'));
    const open = tog.getAttribute('aria-expanded') !== 'true';
    tog.setAttribute('aria-expanded', String(open));
    if (panel) panel.classList.toggle('is-open', open);
  }
});

/* ---------- Back to top: one IntersectionObserver, no scroll listener ---------- */
const backTop = doc.querySelector('[data-back-top]');
if (backTop && 'IntersectionObserver' in window) {
  const mark = doc.createElement('div');
  mark.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:700px;pointer-events:none;visibility:hidden';
  mark.setAttribute('aria-hidden', 'true');
  doc.body.prepend(mark);
  new IntersectionObserver(([entry]) => backTop.classList.toggle('is-visible', !entry.isIntersecting)).observe(mark);
  backTop.addEventListener('click', (e) => {
    e.preventDefault();
    const smooth = !matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: smooth ? 'smooth' : 'auto' });
    const main = doc.getElementById('MainContent');
    if (main) main.focus({ preventScroll: true });
  });
}

/* ---------- Product quote button: popup dialog or smooth scroll to the inquiry form ---------- */
doc.addEventListener('click', (e) => {
  const t = e.target instanceof Element ? e.target : null;
  if (!t) return;
  if (t.closest('[data-quote-close]')) { t.closest('dialog')?.close(); return; }
  const opener = t.closest('[data-quote-open]');
  if (!opener) {
    if (t.matches('dialog.quote-modal')) t.close(); // backdrop click
    return;
  }
  const target = doc.getElementById(opener.dataset.quoteOpen);
  if (!target) return; // falls back to the quote page link
  e.preventDefault();
  const intent = opener.dataset.quoteIntent;
  if (intent) {
    const map = { quote: 'Quotation', design: 'Design / artwork', sample: 'Samples' };
    const radio = target.querySelector(`input[name="contact[Request type]"][value="${map[intent] || ''}"]`);
    if (radio) radio.checked = true;
  }
  if (target instanceof HTMLDialogElement) {
    target.showModal();
    target.querySelector('input:not([type=hidden]),textarea')?.focus();
    return;
  }
  if (target instanceof HTMLDetailsElement) target.open = true;
  target.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
  setTimeout(() => target.querySelector('input:not([type=hidden]),textarea')?.focus({ preventScroll: true }), 500);
});
// After a contact form posts, Shopify reloads the page and marks EVERY contact form on it as posted.
// Shopify adds the submitted form's id as the URL hash, so keep the result only on that form,
// reopen the popup only if the popup was the one used, then clean the URL so a refresh shows a fresh form.
(() => {
  const posted = decodeURIComponent(location.hash.slice(1));
  const resetForm = (form) => {
    form.querySelectorAll('[data-form-status]').forEach((n) => n.remove());
    const f = form.querySelector('[data-form-fields]');
    if (f) f.hidden = false;
  };
  const statusForms = [...doc.querySelectorAll('form.quote-form')].filter((f) => f.querySelector('[data-form-status]'));
  if (!statusForms.length) return;
  const target = statusForms.find((f) => f.id === posted) || (statusForms.length === 1 ? statusForms[0] : null);
  statusForms.forEach((f) => { if (f !== target) resetForm(f); });
  if (target) {
    const dlg = target.closest('dialog.quote-modal');
    if (dlg) dlg.showModal();
    const det = target.closest('details');
    if (det) det.open = true;
  }
  if (target && target.querySelector('[data-form-success]')) {
    const url = new URL(location.href);
    url.searchParams.delete('contact_posted');
    url.hash = '';
    history.replaceState(history.state, '', url.pathname + url.search);
  }
})();
doc.addEventListener('click', (e) => {
  const again = e.target instanceof Element && e.target.closest('[data-form-again]');
  if (!again) return;
  const form = again.closest('form');
  form.querySelectorAll('[data-form-status]').forEach((n) => n.remove());
  const f = form.querySelector('[data-form-fields]');
  if (f) { f.hidden = false; f.querySelector('input:not([type=hidden]):not([data-hp]),textarea')?.focus(); }
});

/* ---------- WhatsApp label: show once after a delay, hide when clicked ---------- */
const waBtn = doc.querySelector('[data-wa-label]');
if (waBtn) {
  const delay = Number(waBtn.dataset.waLabel) * 1000;
  setTimeout(() => waBtn.classList.add('is-labelled'), delay);
  setTimeout(() => waBtn.classList.remove('is-labelled'), delay + 8000);
}

/* ---------- Grid / list view switch (collection + search), remembered per visitor ---------- */
function syncView(wrap) {
  let v = null;
  try { v = localStorage.getItem('swb-view-' + wrap.dataset.viewScope); } catch (e) { /* storage blocked */ }
  if (v === 'grid' || v === 'list') wrap.dataset.view = v;
  wrap.querySelectorAll('[data-view-set]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.viewSet === wrap.dataset.view)));
}
doc.querySelectorAll('[data-view-scope]').forEach(syncView);
doc.addEventListener('click', (e) => {
  const b = e.target instanceof Element && e.target.closest('[data-view-set]');
  if (!b) return;
  const wrap = b.closest('[data-view-scope]');
  if (!wrap) return;
  wrap.dataset.view = b.dataset.viewSet;
  try { localStorage.setItem('swb-view-' + wrap.dataset.viewScope, b.dataset.viewSet); } catch (err) { /* storage blocked */ }
  syncView(wrap);
});
doc.addEventListener('swb:facets-updated', () => doc.querySelectorAll('[data-view-scope]').forEach(syncView));

/* ---------- Product card quick view (module loads on first click) ---------- */
doc.addEventListener('click', (e) => {
  const b = e.target instanceof Element && e.target.closest('[data-quick-view]');
  if (!b) return;
  e.preventDefault();
  load('quickview').then((m) => (m ? m.default(b.dataset.quickView) : (location.href = b.dataset.quickView)));
});

/* ---------- Boot ---------- */
initModules();
if (root.hasAttribute('data-debug')) load('debug').then((m) => m && m.default());

// Theme editor: re-init modules when a section is re-rendered
doc.addEventListener('shopify:section:load', (e) => initModules(e.target));
